<template>
  <div>
    <b-row>
      <b-form-group :label="$t('lang.label_certificate_certUsageType')" label-for="input-usage-type" class="col-md-6 asterisk-label">
        <b-form-select id="input-usage-type" required v-model="cert.certUsageType" class="custom-select" @change="onChange($event)">
          <b-form-select-option :value="null">{{ $t("lang.label_pleaseChoose") }}</b-form-select-option>
          <b-form-select-option v-for="item in usageTypes" v-bind:key="item.value" v-b-tooltip :title="item.title" :value="item.value">{{ item.text }}</b-form-select-option>
        </b-form-select>
        <b-form-invalid-feedback>{{ $t("lang.label_certificate_certUsageType_feedback") }}</b-form-invalid-feedback>
      </b-form-group>
    </b-row>
    <div class="row">
      <b-form-group :label="$t('lang.label_certificate_country')" label-for="country" class="col-md-6">
        <b-form-input
          class="country"
          id="country"
          v-model="cert.country"
          type="text"
          :pattern="PATTERN_COUNTRY"
          minlength="2"
          maxlength="2"
          :placeholder="$t('lang.label_placeholder_enterText')"
          :formatter="formatter"
        />
        <b-form-invalid-feedback>Enter a valid two-letter country code in capital letters</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="$t('lang.label_certificate_commonName')" label-for="common-name" class="col-md-6 asterisk-label">
        <b-form-input id="common-name" v-model="cert.commonName" required type="text" maxlength="64" :placeholder="$t('lang.label_placeholder_enterText')" />
        <b-form-invalid-feedback>{{ $t("lang.label_certificate_commonName_required") }}</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="$t('lang.label_certificate_org')" label-for="organization" class="col-md-6">
        <b-form-input id="organization" v-model="cert.org" type="text" maxlength="64" :placeholder="$t('lang.label_placeholder_enterText')" />
      </b-form-group>
      <b-form-group :label="$t('lang.label_certificate_orgUnit')" label-for="organizational-unit" class="col-md-6">
        <b-form-input id="organizational-unit" v-model="cert.orgUnit" type="text" maxlength="64" :placeholder="$t('lang.label_placeholder_enterText')" />
      </b-form-group>
      <b-form-group v-if="cert.certUsageType === 'SMIME'" :label="$t('lang.label_certificate_email')" label-for="email" class="col-md-6 asterisk-label">
        <b-form-input id="email" required v-model="cert.email" type="email" maxlength="255" :placeholder="$t('lang.label_placeholder_email')" />
        <b-form-invalid-feedback>{{ $t("lang.label_placeholder_email") }}</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group v-if="cert.certUsageType === 'TLS_SERVER'" :label="$t('lang.label_certificate_domainName')" label-for="domain-name" class="col-md-6 asterisk-label">
        <b-form-input id="domain-name" required type="text" v-model="cert.domainName" maxlength="255" :placeholder="$t('lang.label_placeholder_enterText')" />
        <b-form-invalid-feedback>{{ $t("lang.label_certificate_domain_required") }}</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="$t('lang.label_certificate_validity')" label-for="valid-for" class="col-md-6 asterisk-label">
        <b-form-select id="valid-for" required v-model="cert.validFor" class="custom-select" :options="validityOptions"></b-form-select>
        <b-form-invalid-feedback>{{ $t("lang.label_certificate_validity_feedback") }}</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        :label="$t('lang.label_certificate_publicKeyAlgorithm')"
        label-for="public-key-algorithm"
        class="col-md-6 asterisk-label"
        v-b-tooltip.noninteractive.bottom.hover="$t('lang.tooltip_certificate_publicKeyAlgorithm')"
      >
        <b-form-select id="public-key-algorithm" required v-model="cert.certPublicKeyAlgorithm" class="custom-select">
          <b-form-select-option :value="null">{{ $t("lang.select_pleaseSelectOption") }}</b-form-select-option>
          <b-form-select-option v-for="item in publicKeyAlgorithms" v-bind:key="item" v-b-tooltip :title="$t('lang.tooltip_certificate_publicKeyAlgorithm_' + item)" :value="item">{{
            $t("lang.enum_publicKeyAlgorithm_" + item)
          }}</b-form-select-option>
        </b-form-select>
        <b-form-invalid-feedback>{{ $t("lang.label_certificate_publicKeyAlgorithm_required") }}</b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
        :label="$t('lang.label_certificate_signatureAlgorithm')"
        label-for="certificate-signature-algorithm"
        class="col-md-6 asterisk-label"
        v-b-tooltip.noninteractive.bottom.hover="$t('lang.tooltip_certificate_signatureAlgorithm')"
      >
        <b-form-input
          v-if="showTextField"
          readonly
          id="certificate-signature-algorithm-text"
          v-model="cert.certSignatureAlgorithm"
          :value="$t('lang.enum_signatureAlgorithm_SHA256_WITH_SPX')"
        ></b-form-input>
        <b-form-select v-else id="certificate-signature-algorithm" required v-model="cert.certSignatureAlgorithm" class="custom-select">
          <b-form-select-option :value="null">{{ $t("lang.select_pleaseSelectOption") }}</b-form-select-option>
          <b-form-select-option v-for="item in signatureAlgorithms" v-bind:key="item" :value="item">{{ $t("lang.enum_signatureAlgorithm_" + item) }}</b-form-select-option>
        </b-form-select>
        <b-form-invalid-feedback>{{ $t("lang.label_certificate_signatureAlgorithm_required") }}</b-form-invalid-feedback>
      </b-form-group>
    </div>
  </div>
</template>
<script>
import CertificateValidity from "../../api/enums/CertificateValidity";
import KeyUsageType from "../../api/enums/KeyUsageType";
import CertificateCryptoType from "../../api/enums/CertificateCryptoType";
import SignatureAlgorithm from "../../api/enums/SignatureAlgorithm";
import API from "@/api";

export default {
  name: "FormGroupCertificate",
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      usageTypes: [
        { value: KeyUsageType.TLS_SERVER, text: "TLS Server", title: this.$t("lang.tooltip_certificate_certUsageType_tlsServer") },
        { value: KeyUsageType.TLS_CLIENT, text: "TLS Client", title: this.$t("lang.tooltip_certificate_certUsageType_tlsClient") },
        { value: KeyUsageType.SMIME, text: "S/MIME", title: this.$t("lang.tooltip_certificate_certUsageType_sMime") }
      ],
      validityOptions: [
        { value: CertificateValidity.THREE_MONTHS, text: "3 Months" },
        { value: CertificateValidity.SIX_MONTHS, text: "6 Months" }
      ],
      showTextField: false,
      isMTGUser: false
    };
  },
  computed: {
    // Forward any changes to the parent component
    cert: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      }
    },
    /* Dropdown options */
    publicKeyAlgorithms() {
      if (this.cert.certCryptoType === CertificateCryptoType.PQC) {
        return ["SPX", "CME"];
      } else if (this.cert.certCryptoType === CertificateCryptoType.TRADITIONAL) {
        return ["RSA", "EC"];
      } else {
        return [];
      }
    },
    signatureAlgorithms() {
      if (this.cert.certCryptoType === CertificateCryptoType.TRADITIONAL) {
        return [SignatureAlgorithm.SHA256_WITH_RSA, SignatureAlgorithm.SHA256_WITH_ECDSA];
      } else {
        return [SignatureAlgorithm.SHA256_WITH_SPX];
      }
    }
  },
  methods: {
    formatter(value) {
      return value.toUpperCase();
    },
    checkIfUserIsMTGUser() {
      this.$store.dispatch("startLoading");
      API.getAccountDetails()
        .then(response => {
          this.$store.dispatch("stopLoading");
          let userMail = response.data.email;
          if (userMail.endsWith("@mtg.de")) {
            this.isMTGUser = true;
          }
        })
        .catch(() => {
          this.$store.dispatch("stopLoading");
        });
    },
    onChange(event) {
      let index = this.validityOptions.findIndex(element => element.value === CertificateValidity.SIX_YEARS);
      if (index !== -1) {
        this.validityOptions.splice(index, 1);
      }

      if (event === "TLS_SERVER" && this.isMTGUser) {
        this.validityOptions.push({ value: CertificateValidity.SIX_YEARS, text: "6 Years" });
      }
    }
  },
  created() {
    this.checkIfUserIsMTGUser();
    // make the signature algorithm field as text  for pqc certificates
    if (this.cert.certCryptoType === CertificateCryptoType.PQC) {
      this.showTextField = true;
      this.cert.certSignatureAlgorithm = SignatureAlgorithm.SHA256_WITH_SPX;
    }
    /* Constants */
    this.PATTERN_COUNTRY =
      "^(A(D|E|F|G|I|L|M|N|O|R|S|T|Q|U|W|X|Z)|B(A|B|D|E|F|G|H|I|J|L|M|N|O|R|S|T|V|W|Y|Z)|C(A|C|D|F|G|H|I|K|L|M|N|O|R|U|V|X|Y|Z)|D(E|J|K|M|O|Z)|E(C|E|G|H|R|S|T)|F(I|J|K|M|O|R)|G(A|B|D|E|F|G|H|I|L|M|N|P|Q|R|S|T|U|W|Y)|H(K|M|N|R|T|U)|I(D|E|Q|L|M|N|O|R|S|T)|J(E|M|O|P)|K(E|G|H|I|M|N|P|R|W|Y|Z)|L(A|B|C|I|K|R|S|T|U|V|Y)|M(A|C|D|E|F|G|H|K|L|M|N|O|Q|P|R|S|T|U|V|W|X|Y|Z)|N(A|C|E|F|G|I|L|O|P|R|U|Z)|OM|P(A|E|F|G|H|K|L|M|N|R|S|T|W|Y)|QA|R(E|O|S|U|W)|S(A|B|C|D|E|G|H|I|J|K|L|M|N|O|R|T|V|Y|Z)|T(C|D|F|G|H|J|K|L|M|N|O|R|T|V|W|Z)|U(A|G|M|S|Y|Z)|V(A|C|E|G|I|N|U)|W(F|S)|Y(E|T)|Z(A|M|W))$";
  }
};
</script>
